import { useEffect, useRef, useState } from "react";

// Autosaves an editable form object to localStorage so work survives refreshes /
// accidental navigation. Restores any saved draft once `ready` becomes true and
// warns (beforeunload) when there are unsaved changes vs the loaded baseline.
//
//   const { restored, reset } = useAutoDraft({ key, value, ready, onRestore });
//
// - key:       unique localStorage key for this form instance
// - value:     plain serializable object representing the current editable state
// - ready:     start tracking once true (e.g. after server data has hydrated)
// - onRestore: (draftObject) => void, called to apply a found draft to your state
export function useAutoDraft({ key, value, ready = true, onRestore }) {
  const baseline = useRef(null);
  const booted = useRef(false);
  const [restored, setRestored] = useState(false);

  // Boot: capture baseline + restore any saved draft.
  useEffect(() => {
    if (!ready || booted.current) return;
    booted.current = true;
    const base = JSON.stringify(value);
    baseline.current = base;
    try {
      const raw = localStorage.getItem(key);
      if (raw && raw !== base) {
        onRestore && onRestore(JSON.parse(raw));
        setRestored(true);
      }
    } catch { /* ignore corrupt draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, key]);

  // Autosave on every change once booted.
  useEffect(() => {
    if (!booted.current || baseline.current === null) return;
    const cur = JSON.stringify(value);
    if (cur !== baseline.current) {
      try { localStorage.setItem(key, cur); } catch { /* quota / private mode */ }
    }
  });

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    const handler = (e) => {
      if (booted.current && baseline.current !== null && JSON.stringify(value) !== baseline.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  });

  // Clear the draft and force a fresh baseline on next ready cycle.
  const reset = () => {
    try { localStorage.removeItem(key); } catch { /* noop */ }
    booted.current = false;
    baseline.current = null;
    setRestored(false);
  };

  return { restored, reset };
}
